import { Fragment, memo, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ListBullets, MagnifyingGlass, Waveform, X } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtClock, makeSpeakerColor } from "@/lib/format";

// Opacity/offset only: animating a blur filter per word was expensive on slower laptops.
const WORD = { hidden: { opacity: 0, y: 3 }, shown: { opacity: 1, y: 0 } };
const PAGE = 300; // lines kept on screen; older ones load on demand
const REGROUP_AFTER_S = 60; // a header (and its time) repeats once a group has run this long, so long stretches keep time anchors
const MIN_QUERY = 2;

const TypeText = ({ text, delay }) => {
  const words = text.split(/(\s+)/);
  return (
    <motion.span initial="hidden" animate="shown" variants={{ shown: { transition: { delayChildren: delay, staggerChildren: 0.03 } } }} data-testid="transcript-typing">
      {words.map((w, i) => (
        <motion.span key={i} variants={WORD} transition={{ duration: 0.22 }}>{w}</motion.span>
      ))}
    </motion.span>
  );
};

export function initials(name) {
  const n = (name || "").trim();
  if (!n || /^unknown/i.test(n)) return "?";
  const num = n.match(/^speaker\s*(\d+)$/i);
  if (num) return `S${num[1]}`;
  const parts = n.split(/\s+/).filter(Boolean).map((p) => Array.from(p));
  const first = parts[0][0];
  return (parts.length > 1 ? first + parts[parts.length - 1][0] : first).toUpperCase();
}

// Consecutive lines by the same speaker share one header. Gaps stand alone. `each` makes every line its own group (search results).
// `cuts` are chapter starts: a group never runs across one, so the chapter note lands exactly where its stretch begins.
export function groupTurns(turns, each = false, cuts = []) {
  const groups = [];
  for (const t of turns) {
    if (t.type === "gap") {
      groups.push({ key: `g${t.seq}`, gap: t });
      continue;
    }
    const last = groups[groups.length - 1];
    const prev = last && !last.gap ? last.items[last.items.length - 1] : null;
    if (!each && prev && prev.speaker === t.speaker && prev.source === t.source && t.t - last.items[0].t <= REGROUP_AFTER_S
        && !cuts.some((c) => prev.t < c && t.t >= c)) {
      last.items.push(t);
    } else {
      groups.push({ key: `t${t.seq}`, speaker: t.speaker, source: t.source, items: [t] });
    }
  }
  return groups;
}

// Keep only the newest `limit` lines. A group the cut falls inside keeps its key, so it doesn't remount as the window slides.
export function trimGroups(groups, limit) {
  const out = [];
  let left = limit;
  for (let i = groups.length - 1; i >= 0 && left > 0; i--) {
    const g = groups[i];
    if (g.gap) {
      out.push(g);
      continue;
    }
    out.push(g.items.length <= left ? g : { ...g, items: g.items.slice(g.items.length - left) });
    left -= g.items.length;
  }
  return out.reverse();
}

const lineCountOf = (turns) => turns.reduce((n, t) => n + (t.type === "turn" ? 1 : 0), 0);

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const Highlight = ({ text, re }) => {
  if (!re) return text;
  // split() with a capture group puts the matches at the odd positions
  return text.split(re).map((part, i) => (i % 2 ? <mark key={i} className="bg-[#F59E0B]/30 text-[var(--o-fg)] px-0.5" data-testid="transcript-match">{part}</mark> : <Fragment key={i}>{part}</Fragment>));
};

// A line plays its entrance once. `played` remembers which lines already did, so remounting (a late chunk regrouping,
// clearing a search, "Show earlier") never replays it. When the typing is done it is swapped for plain text.
const Line = memo(({ turn, color, re, showTime, played }) => {
  const first = useRef(null);
  if (first.current === null) {
    first.current = !!turn._fresh && !re && !played.has(turn.seq);
    played.add(turn.seq);
  }
  const [typing, setTyping] = useState(first.current);
  useEffect(() => {
    if (!first.current) return undefined;
    const words = (turn.text || "").split(/\s+/).length;
    const t = setTimeout(() => setTyping(false), ((turn._delay || 0) + 0.1 + words * 0.03 + 0.6) * 1000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const fresh = typing && !re;
  return (
    <motion.p
      initial={fresh ? { opacity: 0, x: -6 } : false}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.35, delay: fresh ? turn._delay : 0, ease: [0.2, 0.7, 0.2, 1] }}
      className={`group relative mt-1 text-[15px] sm:text-base leading-relaxed text-[var(--o-fg2)] ${fresh ? "land-flash" : ""}`}
      style={fresh ? { animationDelay: `${turn._delay}s` } : undefined}
      data-testid="transcript-turn"
    >
      {fresh && (
        <motion.span
          className="absolute -left-3 top-1 bottom-1 w-[2px]"
          style={{ background: color, transformOrigin: "top" }}
          initial={{ scaleY: 0, opacity: 1 }}
          animate={{ scaleY: [0, 1, 1], opacity: [1, 1, 0] }}
          transition={{ duration: 2.2, delay: turn._delay, times: [0, 0.2, 1] }}
        />
      )}
      {showTime && (
        <>
          <span className="absolute right-full mr-3 top-1 w-10 text-right font-mono text-[10px] text-[var(--o-muted3)] opacity-0 group-hover:opacity-100 transition-opacity select-none" aria-hidden="true">{fmtClock(turn.t)}</span>
          <span className="sr-only">{fmtClock(turn.t)}: </span>
        </>
      )}
      {fresh ? <TypeText text={turn.text} delay={turn._delay + 0.1} /> : <Highlight text={turn.text} re={re} />}
    </motion.p>
  );
});

const Avatar = ({ name, color }) => (
  <span
    className="mt-0.5 h-8 w-8 shrink-0 rounded-full flex items-center justify-center font-mono text-[11px] font-semibold border select-none"
    style={{ color, borderColor: `color-mix(in srgb, ${color} 45%, transparent)`, background: `color-mix(in srgb, ${color} 14%, transparent)` }}
    aria-hidden="true"
  >
    {initials(name)}
  </span>
);

const NUMBERED = /^Speaker \d+$/;

// "Speaker 57" is one voice the transcription couldn't name. Naming it relabels all its lines and helps later chunks.
const NameVoice = ({ label, color, countOf, people, onRename }) => {
  const [open, setOpen] = useState(false);
  const count = open ? countOf(label) : 0;
  const [other, setOther] = useState("");
  const pick = (name) => {
    if (!name.trim()) return;
    setOpen(false);
    setOther("");
    onRename(label, name.trim());
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="font-semibold text-sm underline decoration-dotted underline-offset-4 hover:decoration-solid" style={{ color }} title="Who is this? Name this voice" data-testid="transcript-speaker">
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-none border-[var(--o-border)] bg-[var(--o-bg2)] text-[var(--o-fg)] p-3 space-y-3" data-testid="name-voice">
        <div>
          <div className="text-sm font-semibold">Who is {label}?</div>
          <div className="text-[11px] text-[var(--o-muted2)] mt-0.5">Renames its {count} line{count === 1 ? "" : "s"}. Picking someone already in the transcript merges them.</div>
        </div>
        {people.length > 0 && (
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto thin-scroll">
            {people.map((n) => (
              <button key={n} onClick={() => pick(n)} className="px-2 py-1 text-xs border border-[var(--o-border)] hover:bg-[var(--o-surface)] hover:border-[var(--o-border-strong)]" data-testid="name-voice-person">{n}</button>
            ))}
          </div>
        )}
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); pick(other); }}>
          <input value={other} onChange={(e) => setOther(e.target.value)} maxLength={60} autoComplete="off" placeholder="Someone else" className="flex-1 min-w-0 bg-[var(--o-bg)] border border-[var(--o-border)] px-2 py-1.5 text-xs outline-none focus:border-[var(--o-fg)]" data-testid="name-voice-input" />
          <button type="submit" disabled={!other.trim()} className="px-3 text-xs font-semibold bg-[var(--o-fg)] text-[var(--o-bg)] disabled:opacity-40" data-testid="name-voice-save">Name</button>
        </form>
      </PopoverContent>
    </Popover>
  );
};

const Group = memo(({ group, color, re, played, naming }) => {
  if (group.gap) {
    return (
      <div className="flex items-center gap-4 py-3 px-2" data-testid={`transcript-gap-${group.gap.chunk}`}>
        <span className="font-mono text-xs text-[var(--o-muted3)] w-12 shrink-0">{fmtClock(group.gap.t)}</span>
        <div className="flex-1 border-t border-dashed border-[var(--o-border-strong)]" />
        <span className="font-mono text-[11px] text-[var(--o-muted2)]">{group.gap.track === "me" ? "your mic" : group.gap.track === "them" ? "call audio" : "audio"} · chunk {group.gap.chunk} not transcribed</span>
        <div className="w-10 border-t border-dashed border-[var(--o-border-strong)]" />
      </div>
    );
  }
  const first = group.items[0];
  return (
    <section className="grid grid-cols-[2rem_1fr] gap-x-3 sm:gap-x-4 py-3 px-2 hover:bg-[var(--o-bg2)] transition-colors" data-testid="transcript-group">
      <Avatar name={group.speaker} color={color} />
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          {naming && NUMBERED.test(group.speaker || "") ? (
            <NameVoice label={group.speaker} color={color} countOf={naming.countOf} people={naming.people} onRename={naming.onRename} />
          ) : (
            <span className="font-semibold text-sm" style={{ color }} data-testid="transcript-speaker">{group.speaker || "Unknown"}</span>
          )}
          <span className="font-mono text-xs text-[var(--o-muted3)]">{fmtClock(first.t)}</span>
          {group.source === "sarvam" && (
            <span className="font-mono text-[10px] border border-[#F59E0B]/40 text-[#F59E0B] px-1 py-px" title="Gemini could not transcribe this chunk; Sarvam filled it in without speaker names" data-testid="transcript-fallback-badge">SARVAM</span>
          )}
        </div>
        {group.items.map((t, i) => (
          <Line key={t.seq} turn={t} color={color} re={re} showTime={i > 0} played={played} />
        ))}
      </div>
    </section>
  );
});

// Chapter notes, written every few minutes, sit in the transcript where their stretch begins.
const ChapterNote = memo(({ c }) => (
  <div className="mt-6 mb-2 mx-2 border-l-2 border-[var(--o-fg)] pl-4 py-1" data-chapter={c.n} data-testid="transcript-chapter">
    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--o-muted3)]">Chapter {c.n} · {fmtClock(c.start)}–{fmtClock(c.end)}</div>
    <div className="font-display text-base font-bold mt-0.5" data-testid="transcript-chapter-title">{c.title}</div>
    {c.summary && <p className="text-sm text-[var(--o-muted)] mt-1 leading-relaxed">{c.summary}</p>}
    {c.points?.length > 0 && (
      <ul className="mt-1.5 space-y-0.5">
        {c.points.map((pt, i) => <li key={i} className="text-xs text-[var(--o-muted2)]">{pt}</li>)}
      </ul>
    )}
  </div>
));

// Which chapter notes go before which group: each before the first group at or after its start. A chapter that starts
// before the first line on screen (its lines are trimmed away) is left out; the chapter list still reaches it.
export function placeChapters(groups, chapters, allVisible) {
  const at = new Map();
  const sorted = [...(chapters || [])].sort((a, b) => a.start - b.start);
  let i = 0;
  groups.forEach((g, gi) => {
    const t = g.gap ? g.gap.t : g.items[0].t;
    const here = [];
    while (i < sorted.length && sorted[i].start <= t) here.push(sorted[i++]);
    if (here.length && (gi > 0 || allVisible)) at.set(g.key, here);
  });
  return at;
}

const ChapterList = ({ chapters, onJump }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="h-9 sm:h-8 px-2.5 flex items-center gap-1.5 border border-[var(--o-border)] font-mono text-[11px] text-[var(--o-muted)] hover:text-[var(--o-fg)] hover:bg-[var(--o-surface)] transition-colors whitespace-nowrap" data-testid="chapters-button">
        <ListBullets size={13} /> Chapters · {chapters.length}
      </button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-80 max-h-[60vh] overflow-y-auto thin-scroll rounded-none border-[var(--o-border)] bg-[var(--o-bg2)] text-[var(--o-fg)] p-2" data-testid="chapters-list">
      {chapters.map((c) => (
        <button key={c.n} onClick={() => onJump(c)} className="w-full text-left px-3 py-2 hover:bg-[var(--o-surface)] transition-colors" data-testid="chapters-item">
          <div className="font-mono text-[10px] text-[var(--o-muted3)]">{fmtClock(c.start)}–{fmtClock(c.end)}</div>
          <div className="text-sm font-semibold">{c.title}</div>
          {c.summary && <div className="text-xs text-[var(--o-muted2)] line-clamp-2 mt-0.5">{c.summary}</div>}
        </button>
      ))}
    </PopoverContent>
  </Popover>
);

const Transcribing = ({ count }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-5 py-4 px-2 font-mono text-xs text-[var(--o-muted2)]" data-testid="transcript-processing">
    <span className="w-8 flex items-end gap-[3px] h-4 text-[var(--o-fg)]">
      {[0, 0.15, 0.3, 0.45, 0.6].map((d) => <span key={d} className="eq-bar" style={{ animationDelay: `${d}s` }} />)}
    </span>
    <span>Transcribing {count > 1 ? `${count} chunks` : "the latest chunk"}<span className="type-caret" /></span>
  </motion.div>
);

export const Transcript = memo(({ turns, participants, processing, mode, chapters: chaptersIn, onRenameSpeaker }) => {
  // Every poll brings a fresh chapters array; keep one identity until a chapter is actually added, so the lines don't regroup.
  const chapterKey = (chaptersIn || []).map((c) => `${c.n}:${c.start}:${c.title}`).join("|");
  const chapters = useMemo(() => chaptersIn || [], [chapterKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const ref = useRef(null);
  const searchRef = useRef(null);
  const [pinned, setPinned] = useState(true);
  const [limit, setLimit] = useState(PAGE);
  const [query, setQuery] = useState("");
  const deferred = useDeferredValue(query.trim());
  const searching = deferred.length >= MIN_QUERY;
  const people = participants.map((p) => p.name).join("|");
  const played = useRef(new Set()).current;
  // Colours are handed out in order of first appearance (earliest time) over the WHOLE transcript, rebuilt from the turns
  // each time, so they are the same while scrolling, searching and in the end-of-meeting summary, whatever order chunks arrived in.
  const colorOf = useMemo(() => {
    const pick = makeSpeakerColor(participants);
    const seen = new Set();
    for (const t of turns) {
      if (t.type === "turn" && !seen.has(t.speaker)) {
        seen.add(t.speaker);
        pick(t.speaker);
      }
    }
    return pick;
  }, [people, turns]); // eslint-disable-line react-hooks/exhaustive-deps

  // One pattern for both counting and highlighting, so a line is never counted without a mark (or marked without being counted).
  // Searches the words spoken, not speaker labels: "Unknown" or "Me" would otherwise match nearly every line.
  const re = useMemo(() => (searching ? new RegExp(`(${escapeRe(deferred)})`, "giu") : null), [searching, deferred]);
  const matches = useMemo(() => {
    if (!re) return [];
    const test = new RegExp(re.source, "iu");
    return turns.filter((t) => t.type === "turn" && test.test(t.text || ""));
  }, [turns, re]);

  const lineTotal = searching ? matches.length : lineCountOf(turns);
  const cuts = useMemo(() => chapters.map((c) => c.start), [chapters]);
  const groups = useMemo(
    () => (searching ? groupTurns(matches.slice(-PAGE), true) : trimGroups(groupTurns(turns, false, cuts), limit)),
    [searching, matches, turns, limit, cuts],
  );
  const hidden = Math.max(0, lineTotal - (searching ? PAGE : limit));

  // Naming a numbered voice: everyone already in the transcript or the participant list, plus how many lines it has.
  // The object handed to every group only changes when the list of names does, so a new chunk doesn't redraw them all.
  const counts = useRef(new Map());
  const nameList = useMemo(() => {
    const c = new Map();
    const names = new Set(participants.map((p) => p.name).filter(Boolean));
    for (const t of turns) {
      if (t.type !== "turn") continue;
      c.set(t.speaker, (c.get(t.speaker) || 0) + 1);
      if (t.speaker && !NUMBERED.test(t.speaker) && !/^unknown/i.test(t.speaker) && t.speaker !== "Me") names.add(t.speaker);
    }
    counts.current = c;
    return [...names].sort((a, b) => a.localeCompare(b)).join("\u0000");
  }, [turns, people]); // eslint-disable-line react-hooks/exhaustive-deps
  const naming = useMemo(
    () => (onRenameSpeaker ? { people: nameList ? nameList.split("\u0000") : [], countOf: (l) => counts.current.get(l) || 0, onRename: onRenameSpeaker } : null),
    [nameList, onRenameSpeaker],
  );
  const chapterAt = useMemo(() => (searching ? new Map() : placeChapters(groups, chapters, hidden === 0)), [searching, groups, chapters, hidden]);

  // Jumping to a chapter loads enough earlier lines to include it, then scrolls to its note.
  const [jump, setJump] = useState(null);
  const jumpTo = (c) => {
    setQuery("");
    setPinned(false);
    const after = turns.filter((t) => t.type === "turn" && t.t >= c.start).length;
    setLimit((l) => Math.max(l, after + 5));
    setJump(c.n);
  };
  useEffect(() => {
    if (jump == null || searching) return;
    const el = ref.current?.querySelector(`[data-chapter="${jump}"]`);
    if (el) {
      el.scrollIntoView({ block: "start" });
      setJump(null);
    }
  }, [jump, groups, searching]);

  useEffect(() => {
    if (!searching && pinned && ref.current) ref.current.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" });
  }, [turns.length, pinned, processing, searching]);

  // Entering a search starts at the top of the results; leaving it returns to the live end.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (searching) el.scrollTo({ top: 0 });
    else {
      setPinned(true);
      el.scrollTo({ top: el.scrollHeight });
    }
  }, [searching]);

  // "/" jumps to search, unless you are typing somewhere.
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey && tag !== "INPUT" && tag !== "TEXTAREA" && !e.target?.isContentEditable) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onScroll = () => {
    const el = ref.current;
    setPinned(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
  };


  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      {turns.length > 0 && (
        <div className="shrink-0 flex items-center gap-3 px-4 sm:px-8 py-2 border-b border-[var(--o-border-soft)]" role="search">
          <label className="flex items-center gap-2 flex-1 min-w-0 max-w-sm h-9 sm:h-8 px-2.5 border border-[var(--o-border)] focus-within:border-[var(--o-fg)] transition-colors">
            <MagnifyingGlass size={14} className="text-[var(--o-muted2)] shrink-0" aria-hidden="true" />
            <input
              ref={searchRef}
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setQuery("");
                  e.currentTarget.blur();
                }
              }}
              placeholder="Search transcript   ( / )"
              aria-label="Search transcript"
              className="flex-1 min-w-0 bg-transparent text-base sm:text-sm outline-none placeholder:text-[var(--o-muted3)]"
              data-testid="transcript-search"
            />
            {query && (
              <button onClick={() => { setQuery(""); searchRef.current?.focus(); }} className="text-[var(--o-muted2)] hover:text-[var(--o-fg)] p-2 -m-2" aria-label="Clear search" data-testid="transcript-search-clear">
                <X size={13} />
              </button>
            )}
          </label>
          <span className="font-mono text-[11px] text-[var(--o-muted2)] whitespace-nowrap" aria-live="polite" data-testid="transcript-search-count">
            {searching ? `${matches.length} match${matches.length === 1 ? "" : "es"}` : `${lineTotal} lines`}
          </span>
          {chapters.length > 0 && <div className="ml-auto"><ChapterList chapters={chapters} onJump={jumpTo} /></div>}
        </div>
      )}
      <div ref={ref} onScroll={onScroll} className="thin-scroll flex-1 min-h-0 overflow-y-auto px-4 sm:px-8 py-4" data-testid="transcript-scroll">
        {turns.length === 0 && (
          <div className="h-full flex flex-col items-start justify-end pb-10 text-[var(--o-muted3)]" data-testid="transcript-empty">
            <Waveform size={28} />
            <p className="font-display text-2xl font-bold text-[var(--o-muted2)] mt-3">{mode === "replay" ? "Transcribing the recording…" : "Listening. The first chunk lands in under a minute."}</p>
            <p className="text-sm mt-2">Audio is transcribed in chunks; each chunk is deleted after transcription.</p>
          </div>
        )}
        {searching && matches.length === 0 && (
          <p className="py-10 text-sm text-[var(--o-muted2)]" data-testid="transcript-no-matches">No lines match “{deferred}”.</p>
        )}
        {hidden > 0 && (
          searching ? (
            <p className="py-2 mb-2 font-mono text-[11px] text-[var(--o-muted2)]" data-testid="transcript-matches-capped">Showing the latest {PAGE} of {matches.length} matches. Narrow the search to see the rest.</p>
          ) : (
            <button onClick={() => setLimit((l) => l + PAGE)} className="w-full py-2 mb-2 font-mono text-[11px] text-[var(--o-muted2)] border border-dashed border-[var(--o-border)] hover:text-[var(--o-fg)] transition-colors" data-testid="transcript-show-earlier">
              Show {Math.min(PAGE, hidden)} earlier lines · {hidden} hidden
            </button>
          )
        )}
        {groups.map((g) => (
          <Fragment key={g.key}>
            {chapterAt.get(g.key)?.map((c) => <ChapterNote key={`c${c.n}`} c={c} />)}
            <Group group={g} color={g.gap ? undefined : colorOf(g.speaker)} re={re} played={played} naming={naming} />
          </Fragment>
        ))}
        {!searching && processing > 0 && <Transcribing count={processing} />}
      </div>
      {!pinned && !searching && (
        <button onClick={() => setPinned(true)} className="absolute bottom-5 right-6 flex items-center gap-2 bg-[var(--o-fg)] text-[var(--o-bg)] text-xs font-semibold px-3 py-2 hover:opacity-90 transition-colors" data-testid="jump-to-latest-button">
          <ArrowDown size={13} weight="bold" /> Latest
        </button>
      )}
    </div>
  );
});
